import React from 'react';
import { Box, Typography, Breadcrumbs as MuiBreadcrumbs, Link } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

/**
 * Breadcrumbs component that tracks navigation history
 * Stores breadcrumb trail in location state to maintain context
 */
const Breadcrumbs = ({ currentLabel, currentIcon }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get breadcrumb trail from location state, or create default
  const breadcrumbTrail = location.state?.breadcrumbTrail || [];
  
  const handleBreadcrumbClick = (index) => {
    // Navigate to the clicked breadcrumb and restore its trail
    const targetBreadcrumb = breadcrumbTrail[index];
    const newTrail = breadcrumbTrail.slice(0, index);
    
    navigate(targetBreadcrumb.path, {
      state: {
        ...targetBreadcrumb.state,
        breadcrumbTrail: newTrail
      }
    });
  };

  if (breadcrumbTrail.length === 0) {
    // No breadcrumbs to show
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#666' }} />}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
        }}
      >
        {breadcrumbTrail.map((crumb, index) => (
          <Link
            key={index}
            component="button"
            onClick={() => handleBreadcrumbClick(index)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#666',
              textDecoration: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontFamily: '"Georgia", serif',
              '&:hover': {
                color: '#333',
                textDecoration: 'underline',
              },
              border: 'none',
              background: 'none',
              padding: 0,
            }}
          >
            {crumb.icon === 'home' && <HomeIcon sx={{ fontSize: '1rem' }} />}
            {crumb.icon && crumb.icon !== 'home' && <span>{crumb.icon}</span>}
            <span>{crumb.label}</span>
          </Link>
        ))}
        
        {/* Current page (not clickable) */}
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#333',
            fontSize: '0.875rem',
            fontFamily: '"Georgia", serif',
            fontWeight: 'bold',
          }}
        >
          {currentIcon && <span>{currentIcon}</span>}
          <span>{currentLabel}</span>
        </Typography>
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
